// Maps between the Google/Azure config form shapes and sync-api v2 (SM-API), the
// counterpart to the SIS mapper. Google/Azure are device-level singletons: reads
// resolve the one node of that syncType; writes create it if absent, else update.
//
// NOTE (Google): sync-api v2 has no field for `allow_insecure_chrome`,
// `classroom_extension_login`, or the customer/service-account OAuth credential, so
// those are intentionally dropped on the SM-API path (left blank) — a known gap.
import {
  AzureProviderConfig,
  AzureSyncConfigInput,
  CreateSyncConfigurationInput,
  CredentialState,
  createSyncConfiguration,
  GoogleProviderConfig,
  GoogleSyncConfigInput,
  MergeConfigInput,
  MergeConfigNode,
  resolveSyncIdByType,
  SyncConfigurationNode,
  SyncType,
  UpdateSyncConfigurationInput,
  updateSyncConfiguration,
} from "../../../utils/api/SyncApi";
import { UserMergeKey } from "./ConfigForm/MergeOptions";
// Type-only imports (erased) so this module has no runtime dependency on the config
// components that import it back.
import type { AzureConfigInputs, GroupNameField } from "./AzureConfiguration";
import type { GoogleConfigInputs } from "./GoogleConfiguration";
import type { MergeConfig } from "../sis-integration/SisIntegration";

const splitCsv = (s?: string): string[] =>
  s
    ? s
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean)
    : [];

// --- merge helpers (shared shape with SIS: priority/attachOnly + email|computed) ---
// Also used by the LDAP mapper, whose merge block has the same shape.

export const mergeNodeToConfig = (merge?: MergeConfigNode | null): MergeConfig | undefined => {
  if (!merge) return undefined;
  const mc: MergeConfig = { priority: merge.priority, attachOnly: merge.attachOnly };
  if (merge.method?.expression !== undefined) mc.computed = { expression: merge.method.expression };
  else if (merge.method?.stripped !== undefined) mc.email = { stripped: merge.method.stripped };
  return mc;
};

export const buildMergeConfig = (priority: number, key: UserMergeKey, expression: string | undefined, attachOnly: boolean): MergeConfig => {
  const mc: MergeConfig = { priority, attachOnly };
  if (key === UserMergeKey.Computed) mc.computed = { expression: expression || "" };
  else mc.email = { stripped: key === UserMergeKey.EmailStripped };
  return mc;
};

export const mergeConfigToInput = (mc: MergeConfig): MergeConfigInput => {
  const method = mc.computed ? { computed: { expression: mc.computed.expression } } : { email: { stripped: mc.email?.stripped ?? false } };
  return { priority: mc.priority, attachOnly: mc.attachOnly ?? false, method };
};

export const mergeKeyFromConfig = (mc?: MergeConfig): UserMergeKey =>
  mc?.computed ? UserMergeKey.Computed : mc?.email?.stripped ? UserMergeKey.EmailStripped : UserMergeKey.Email;

/** True when the sync's single credential is set (drives the "Linked" state). */
export const isCredentialLinked = (node: SyncConfigurationNode): boolean => {
  const config = node.config as (GoogleProviderConfig | AzureProviderConfig) | undefined;
  return config?.credential?.state === CredentialState.Set;
};

// --- Azure -----------------------------------------------------------------

export const mapNodeToAzureInputs = (node: SyncConfigurationNode): AzureConfigInputs => {
  const config = node.config as AzureProviderConfig | undefined;
  const merge = mergeNodeToConfig(node.merge);
  const domainFilterEnabled = config?.domainFilter.enabled ?? false;
  return {
    enabled: node.enabled,
    strip_domain: config?.stripDomain.enabled ?? false,
    strip_domain_value: (config?.stripDomain.values ?? []).join(","),
    domain_filter_enabled: domainFilterEnabled,
    domain_filter_value: (config?.domainFilter.values ?? []).join(","),
    sync_hidden_group_memberships: config?.syncHiddenGroupMemberships ?? false,
    group_name_field: (config?.groupNameField === "DESCRIPTION" ? "description" : "displayName") as GroupNameField,
    user_merge_priority: merge?.priority || 50,
    user_merge_expression: merge?.computed?.expression || undefined,
    user_merge_key: mergeKeyFromConfig(merge),
    user_merge_attach_only: merge?.attachOnly || false,
    azuread_sync_id: node.id,
    ignore_empty_groups: domainFilterEnabled || (config?.ignoreEmptyGroups ?? false),
  };
};

const mapAzureInputsToBlock = (input: AzureConfigInputs): AzureSyncConfigInput => ({
  syncHiddenGroupMemberships: input.sync_hidden_group_memberships,
  groupNameField: input.group_name_field === "description" ? "DESCRIPTION" : "DISPLAY_NAME",
  stripDomain: { enabled: input.strip_domain, values: splitCsv(input.strip_domain_value) },
  domainFilter: { enabled: input.domain_filter_enabled, values: splitCsv(input.domain_filter_value) },
  ignoreEmptyGroups: input.ignore_empty_groups ?? false,
});

/** Create (if none) or update the singleton Azure sync from the form inputs. */
export const saveAzureSync = async (input: AzureConfigInputs): Promise<void> => {
  const merge = mergeConfigToInput(
    buildMergeConfig(input.user_merge_priority, input.user_merge_key, input.user_merge_expression, input.user_merge_attach_only)
  );
  await saveSingletonSync(
    SyncType.Azure,
    "Microsoft Entra ID",
    { enabled: input.enabled, merge, azure: mapAzureInputsToBlock(input) },
    input.azuread_sync_id
  );
};

// --- Google ----------------------------------------------------------------

export const mapNodeToGoogleInputs = (node: SyncConfigurationNode): GoogleConfigInputs => {
  const config = node.config as GoogleProviderConfig | undefined;
  const merge = mergeNodeToConfig(node.merge);
  return {
    enabled: node.enabled,
    sync_ous: config?.syncOrgUnits ?? false,
    // Not modelled in sync-api v2 — left blank on the SM-API path.
    allow_insecure_chrome: false,
    classroom_extension_login: false,
    sync_classrooms: config?.syncClassrooms ?? false,
    sync_by_id: config?.syncGroupsByID ?? false,
    admin_username: config?.adminEmail ?? "",
    oauth_client_id: "",
    oauth_client_secret: "",
    ignore_empty_groups: config?.ignoreEmptyGroups ?? false,
    ignore_empty_classrooms: config?.ignoreEmptyClassrooms ?? false,
    sync_id: node.id,
    strip_domain: config?.stripDomain.enabled ?? false,
    strip_domain_values: (config?.stripDomain.values ?? []).join(","),
    domain_filter: config?.domainFilter.enabled ?? false,
    domain_filter_values: (config?.domainFilter.values ?? []).join(","),
    ou_filter: config?.orgUnitFilter.enabled ?? false,
    ou_filter_value: config?.orgUnitFilter.value ?? "",
    user_merge_priority: merge?.priority || 50,
    user_merge_expression: merge?.computed?.expression || undefined,
    user_merge_key: mergeKeyFromConfig(merge),
    user_merge_attach_only: merge?.attachOnly || false,
  };
};

const mapGoogleInputsToBlock = (input: GoogleConfigInputs): GoogleSyncConfigInput => ({
  adminEmail: input.admin_username,
  syncOrgUnits: input.sync_ous,
  syncClassrooms: input.sync_classrooms,
  syncGroupsByID: input.sync_by_id,
  stripDomain: { enabled: input.strip_domain, values: splitCsv(input.strip_domain_values) },
  domainFilter: { enabled: input.domain_filter, values: splitCsv(input.domain_filter_values) },
  orgUnitFilter: { enabled: input.ou_filter, value: input.ou_filter ? input.ou_filter_value || "" : "" },
  // Enabling the OU filter forces both ignore-empty flags on (matches server behaviour).
  ignoreEmptyGroups: input.ou_filter ? true : input.ignore_empty_groups,
  ignoreEmptyClassrooms: input.ou_filter ? true : input.ignore_empty_classrooms,
});

/** Create (if none) or update the singleton Google sync from the form inputs. */
export const saveGoogleSync = async (input: GoogleConfigInputs): Promise<void> => {
  const merge = mergeConfigToInput(
    buildMergeConfig(input.user_merge_priority, input.user_merge_key, input.user_merge_expression, input.user_merge_attach_only)
  );
  await saveSingletonSync(
    SyncType.Google,
    "Google Workspace",
    { enabled: input.enabled, merge, google: mapGoogleInputsToBlock(input) },
    input.sync_id
  );
};

// --- shared save (singleton create-or-update) ------------------------------

interface SingletonPartial {
  enabled: boolean;
  merge: MergeConfigInput;
  google?: GoogleSyncConfigInput;
  azure?: AzureSyncConfigInput;
}

const saveSingletonSync = async (syncType: SyncType, defaultName: string, partial: SingletonPartial, knownId?: string): Promise<void> => {
  // The form already holds the singleton's id after its initial load, so an
  // update can PATCH straight away. Only fall back to a resolve GET when there
  // is no id yet — a genuine first-time create.
  const id = knownId || (await resolveSyncIdByType(syncType));
  if (id) {
    await updateSyncConfiguration(id, partial as UpdateSyncConfigurationInput);
  } else {
    await createSyncConfiguration({ syncType, name: defaultName, ...partial } as CreateSyncConfigurationInput);
  }
};
